import { AfterViewInit, Component, ElementRef, Inject, Injector, OnDestroy, PLATFORM_ID, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ServiceItem, ServiceProcessStep } from './services.models';
import { ServicesFinalCtaSectionComponent } from './sections/services-final-cta-section/services-final-cta-section.component';
import { ServicesGridSectionComponent } from './sections/services-grid-section/services-grid-section.component';
import { ServicesHeroSectionComponent } from './sections/services-hero-section/services-hero-section.component';
import { ServicesProcessSectionComponent } from './sections/services-process-section/services-process-section.component';
import { runAfterHydration } from '../shared/run-after-hydration';
import { initPageScrollReveal } from '../shared/scroll-reveal.util';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [
    CommonModule,
    ServicesFinalCtaSectionComponent,
    ServicesGridSectionComponent,
    ServicesHeroSectionComponent,
    ServicesProcessSectionComponent
  ],
  templateUrl: './services.component.html',
  styleUrl: './services.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class ServicesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('servicesRoot') servicesRoot!: ElementRef<HTMLElement>;

  readonly services: ServiceItem[] = [
    {
      icon: 'fa fa-bullhorn',
      title: 'Publicidad',
      tag: 'Awareness',
      description: 'Campañas personalizadas que conectan con audiencias reales y potencian tu marca.',
      route: '/servicios/publicidad'
    },
    {
      icon: 'fa fa-laptop',
      title: 'Desarrollo Web',
      tag: 'Web',
      description: 'Sitios y plataformas digitales a medida, optimizadas para rendimiento, experiencia y conversión.',
      route: '/servicios/desarrollo-web'
    },
    {
      icon: 'fa fa-line-chart',
      title: 'Marketing Digital',
      tag: 'Growth',
      description: 'Estrategias para aumentar visibilidad, captar clientes y generar resultados medibles.',
      route: '/servicios/digital-marketing'
    },
    {
      icon: 'fa fa-film',
      title: 'Digital Media Center',
      tag: 'Contenido',
      description: 'Contenido, campañas, redes y activos digitales trabajando desde una misma operación.',
      route: '/servicios/digital-media-center'
    },
    {
      icon: 'fa fa-users',
      title: 'Social Media',
      tag: 'Comunidad',
      description: 'Estrategias y gestión profesional para conectar marcas con comunidades auténticas.',
      route: '/servicios/social-media'
    },
    {
      icon: 'fa fa-mobile',
      title: 'Desarrollo de Apps',
      tag: 'Producto',
      description: 'Experiencias móviles y plataformas empresariales diseñadas para impactar, fidelizar y escalar.',
      route: '/servicios/desarrollo-apps'
    },
    {
      icon: 'fa fa-database',
      title: 'Data Science',
      tag: 'Data',
      description: 'Transformamos datos en conocimiento accionable para decisiones estratégicas.',
      route: '/servicios/data-science'
    },
    {
      icon: 'fa fa-link',
      title: 'Blockchain Solutions',
      tag: 'Innovación',
      description: 'Soluciones blockchain seguras y escalables para nuevos modelos digitales.',
      route: '/servicios/blockchain'
    },
    {
      icon: 'fa fa-headphones',
      title: 'Call Center',
      tag: 'Operación',
      description: 'Atención al cliente y seguimiento comercial con procesos integrados.',
      route: '/servicios/call-center'
    },
    {
      icon: 'fa fa-bar-chart',
      title: 'Business Intelligence',
      tag: 'BI',
      description: 'Tableros y análisis para convertir información en decisiones inteligentes.',
      route: '/servicios/business-intelligence'
    },
    {
      icon: 'fa fa-credit-card',
      title: 'Fintech',
      tag: 'Finanzas',
      description: 'Soluciones financieras digitales con tecnología, experiencia y escalabilidad.',
      route: '/servicios/fintech'
    }
  ];

  readonly processSteps: ServiceProcessStep[] = [
    {
      icon: 'bi-search',
      title: 'Diagnóstico',
      description: 'Entendemos tu negocio, canales, audiencia y objetivos comerciales.'
    },
    {
      icon: 'bi-diagram-3',
      title: 'Arquitectura',
      description: 'Definimos servicios, prioridades, tiempos, responsables e indicadores.'
    },
    {
      icon: 'bi-lightning-charge',
      title: 'Ejecución',
      description: 'Activamos campañas, productos, contenido, datos y tecnología con foco comercial.'
    },
    {
      icon: 'bi-graph-up-arrow',
      title: 'Optimización',
      description: 'Medimos, aprendemos y mejoramos el sistema para escalar resultados.'
    }
  ];

  private gsapCleanups: Array<() => void> = [];

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private injector: Injector
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      runAfterHydration(this.injector, () => void this.setupScrollRevealAnimation());
    }
  }

  ngOnDestroy(): void {
    this.gsapCleanups.forEach((cleanup) => cleanup());
    this.gsapCleanups = [];
  }

  private async setupScrollRevealAnimation(): Promise<void> {
    const root = this.servicesRoot?.nativeElement;
    if (!root) return;
    await initPageScrollReveal(root, this.gsapCleanups);
  }
}
